import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import { DataTable, DateField, EmptyState, LoadingButton, Notice, PageHeader, useNotify, type DataTableColumn } from '@routes-plan/ui';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { useT, type TextKey } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { formatCurrency } from '../../../utils/format/currency';
import { FilterBar, SelectField } from '../components/ProcessFilters';

interface LateRow {
  id: number;
  idNumber: string;
  fullName: string;
  loanValue: number;
  value: number;
  paymentDate: string;
  balance: number;
  daysLate: string | number;
  route: string;
}

/**
 * Consulta de préstamos con atraso por fecha y rutas (informe `reports/loans/late`). Hoy la usan
 * "Eliminar préstamos" y "Eliminar clientes": muestran qué hay, todavía no eliminan nada.
 */
const LateLoansLookup: React.FC<{ titleKey: TextKey }> = ({ titleKey }) => {
  const t = useT();
  const notify = useNotify();
  const today = nowOperationalDateString();
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(today);
  const [rows, setRows] = useState<LateRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    setPageTitle(titleKey);
    (async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleKey]);

  useEffect(() => {
    setSelectedRouteIds([]);
    if (!officeId) {
      setOfficeRoutes([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        setOfficeRoutes(Array.isArray(data) ? data : []);
      } catch (error) {
        setOfficeRoutes([]);
      }
    })();
  }, [officeId]);

  const search = async () => {
    setSearching(true);
    try {
      const response = await axios.post('/reports/loans/late', { endDate: today, startDate, routeId: selectedRouteIds });
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(
        list.map((r: any) => ({
          id: r.id,
          idNumber: r.loan?.client?.idNumber ?? '',
          fullName: r.loan?.client?.fullName ?? '',
          loanValue: Number(r.loan?.value ?? 0),
          value: Number(r.value ?? 0),
          paymentDate: r.paymentDate ?? '',
          balance: Number(r.balance ?? 0),
          daysLate: r.daysLate ?? '',
          route: r.loan?.route?.name ?? '',
        })),
      );
      setSearched(true);
    } catch (error) {
      notify.error(t('common.loadError'));
    } finally {
      setSearching(false);
    }
  };

  const columns: DataTableColumn<LateRow>[] = [
    { id: 'fullName', header: t('dataDeletion.colClient'), value: (r) => r.fullName, sortable: true },
    { id: 'idNumber', header: t('dataDeletion.colClientId'), value: (r) => r.idNumber, sortable: true, hideBelow: 'sm' },
    { id: 'id', header: t('dataDeletion.colLoanNumber'), value: (r) => r.id, sortable: true, hideBelow: 'md' },
    { id: 'loanValue', header: t('dataDeletion.colLoanValue'), value: (r) => r.loanValue, render: (r) => formatCurrency(r.loanValue), align: 'right', sortable: true },
    { id: 'value', header: t('dataDeletion.colPayments'), value: (r) => r.value, render: (r) => formatCurrency(r.value), align: 'right', hideBelow: 'md' },
    { id: 'paymentDate', header: t('common.date'), value: (r) => r.paymentDate, sortable: true, hideBelow: 'md' },
    { id: 'balance', header: t('dataDeletion.colBalance'), value: (r) => r.balance, render: (r) => formatCurrency(r.balance), align: 'right', sortable: true },
    { id: 'daysLate', header: t('dataDeletion.colDaysLate'), value: (r) => Number(r.daysLate) || 0, align: 'right', sortable: true },
    { id: 'route', header: t('common.route'), value: (r) => r.route, sortable: true, hideBelow: 'sm' },
  ];

  const routeName = (id: string) => officeRoutes.find((r) => String(r.id) === id)?.name ?? id;

  return (
    <HeaderResponsive>
      <PageHeader title={t(titleKey)} description={t('dataDeletion.subtitle')} />
      <Box sx={{ mb: 2 }}>
        <Notice severity="info">{t('dataDeletion.notice')}</Notice>
      </Box>
      <FilterBar>
        <DateField
          id="late-date"
          label={t('dataDeletion.selectDate')}
          value={startDate}
          max={today}
          onChange={(v) => setStartDate(v ?? today)}
          fullWidth
        />
        <SelectField id="late-office" label={t('common.selectOffice')} value={officeId} options={offices} onChange={setOfficeId} />
        <TextField
          id="late-routes"
          select
          fullWidth
          label={t('dataDeletion.routes')}
          value={selectedRouteIds}
          disabled={!officeId}
          SelectProps={{
            multiple: true,
            onChange: (e) => setSelectedRouteIds((e.target.value as string[]).map(String)),
            renderValue: (selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((id) => (
                  <Chip key={id} size="small" label={routeName(id)} />
                ))}
              </Box>
            ),
          }}
        >
          {officeRoutes.map((r) => (
            <MenuItem key={r.id} value={String(r.id)}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 56 }}>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            startIcon={<SearchOutlinedIcon />}
            loading={searching}
            disabled={selectedRouteIds.length === 0}
            onClick={() => void search()}
          >
            {t('common.search')}
          </LoadingButton>
        </Box>
      </FilterBar>
      <DataTable<LateRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        searchable
        exportFileName={t('dataDeletion.exportFileName')}
        ariaLabel={t(titleKey)}
        emptyState={
          <EmptyState
            dense
            icon={<ManageSearchOutlinedIcon />}
            title={searched ? t('dataDeletion.empty') : t('dataDeletion.selectToStart')}
          />
        }
      />
    </HeaderResponsive>
  );
};

export default LateLoansLookup;
